import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add,
  Remove,
  Search,
  Star,
  Schedule,
  Sports,
  SportsFootball,
  SportsBasketball,
  TrendingUp,
  TrendingFlat,
  LocationOn,
  Info,
  Lock,
  EmojiEvents,
  Refresh
} from '@mui/icons-material';
import { SportIcon } from '../utils/sportIcons';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSport } from '../contexts/SportContext';
import { ScheduleGame } from '../types';
import { scheduleAPI, weeksAPI, gamesAPI, tiebreakersAPI, rankingsAPI } from '../services/api';
import { BasketballDayPicker } from '../components/BasketballDayPicker';
import { getSpreadDisplay } from '../utils/gameUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const CreateWeek: React.FC = () => {
  const { selectedSport: sport } = useSport();
  const [weekNumber, setWeekNumber] = useState<number | null>(null);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [calculatedLockTime, setCalculatedLockTime] = useState<Dayjs | null>(null);
  const [availableGames, setAvailableGames] = useState<ScheduleGame[]>([]);
  const [selectedGames, setSelectedGames] = useState<ScheduleGame[]>([]);
  const [gameSpreadSettings, setGameSpreadSettings] = useState<{[gameId: number]: boolean}>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [showTop25Only, setShowTop25Only] = useState(true);
  const [createPermission, setCreatePermission] = useState<{
    canCreate: boolean;
    reason: string;
    message: string;
    lastWinner?: { username: string; week_number: number; year: number };
    nextWeek?: { week_number: number; year: number };
  } | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [tiebreaker1Game, setTiebreaker1Game] = useState<ScheduleGame | null>(null);
  const [tiebreaker2Game, setTiebreaker2Game] = useState<ScheduleGame | null>(null);
  const [createdWeekId, setCreatedWeekId] = useState<number | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [manualSpreads, setManualSpreads] = useState<{[gameId: number]: string}>({});
  const [spreadUpdateLoading, setSpreadUpdateLoading] = useState<{[gameId: number]: boolean}>({});
  const [rankingsUpdateLoading, setRankingsUpdateLoading] = useState(false);
  const [liveRankings, setLiveRankings] = useState<Array<{rank: number; team: string; record?: string}>>([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [rankingsPoll, setRankingsPoll] = useState('Unknown');

  const { user } = useAuth();
  const navigate = useNavigate();

  // Helper function to format team name with ranking
  const formatTeamWithRanking = (teamName: string, ranking?: number) => {
    if (ranking) {
      return `#${ranking} ${teamName}`;
    }
    return teamName;
  };

  // Calculate lock time based on selected games (1 hour before earliest game)
  const calculateLockTime = (games: ScheduleGame[]) => {
    if (games.length === 0) {
      setCalculatedLockTime(null);
      return;
    }

    console.log('🕐 Calculating lock time for games:');
    games.forEach((game, index) => {
      console.log(`  ${index + 1}. ${game.away_team} @ ${game.home_team} - ${dayjs(game.start_date).format('ddd, MMM D @ h:mm A')}`);
    });

    // Find the earliest game time using a more explicit approach
    let earliestGame = games[0];
    let earliestTime = dayjs(games[0].start_date);

    for (let i = 1; i < games.length; i++) {
      const currentGameTime = dayjs(games[i].start_date);
      if (currentGameTime.isBefore(earliestTime)) {
        earliestGame = games[i];
        earliestTime = currentGameTime;
      }
    }

    console.log(`🎯 Earliest game found: ${earliestGame.away_team} @ ${earliestGame.home_team} at ${earliestTime.format('ddd, MMM D @ h:mm A')}`);

    // Set lock time to 1 hour before the earliest game
    const lockTime = earliestTime.subtract(1, 'hour');
    console.log(`🔒 Lock time set to: ${lockTime.format('ddd, MMM D @ h:mm A')}`);
    
    setCalculatedLockTime(lockTime);
  };

  useEffect(() => {
    checkAuthorization();
  }, []);

  // Load live rankings when sport changes
  useEffect(() => {
    loadLiveRankings();
  }, [sport]);

  // Recalculate lock time when selected games change
  useEffect(() => {
    calculateLockTime(selectedGames);
  }, [selectedGames]);

  const checkAuthorization = async () => {
    try {
      setAuthLoading(true);
      const permission = await weeksAPI.canCreateNext();
      setCreatePermission(permission);

      if (!permission.canCreate) {
        setError(permission.message);
        return;
      }

      // If authorized, load current week info and set defaults
      if (permission.nextWeek) {
        setYear(permission.nextWeek.year);
        setWeekNumber(permission.nextWeek.week_number);
      } else {
        await loadCurrentWeekInfo();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check authorization');
    } finally {
      setAuthLoading(false);
    }
  };

  const loadLiveRankings = async () => {
    try {
      setRankingsLoading(true);
      const response = await rankingsAPI.getLiveRankings(sport);

      if (response && response.rankings) {
        setLiveRankings(response.rankings);
        setRankingsPoll(response.poll || 'AP Top 25');
      }
    } catch (err: any) {
      console.error('Error loading live rankings:', err);
      // Fall back to empty rankings on error
      setLiveRankings([]);
      setRankingsPoll('Unavailable');
    } finally {
      setRankingsLoading(false);
    }
  };

  useEffect(() => {
    // Only load games for football - basketball uses the day picker which loads on-demand
    if (year && weekNumber !== null && sport === 'football') {
      loadAvailableGames();
    }
  }, [year, weekNumber, sport, searchTerm, showTop25Only]);

  const loadCurrentWeekInfo = async () => {
    try {
      const currentWeek = await scheduleAPI.getCurrentWeek(sport);
      setYear(currentWeek.year);
      setWeekNumber(currentWeek.week);
    } catch (err) {
      console.error('Failed to load current week:', err);
    }
  };

  const loadAvailableGames = async () => {
    if (weekNumber === null) return;

    try {
      setLoading(true);
      let games;

      if (searchTerm) {
        games = await scheduleAPI.searchGames(sport, year, weekNumber, searchTerm);
      } else {
        games = await scheduleAPI.getGamesWithLines(sport, year, weekNumber);
      }

      // Filter by top 25 if enabled
      const filteredGames = showTop25Only
        ? games.filter((game: ScheduleGame) => game.is_top25)
        : games;

      setAvailableGames(filteredGames);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const handleGameToggle = (game: ScheduleGame) => {
    const isSelected = selectedGames.some(g => g.id === game.id);
    
    if (isSelected) {
      setSelectedGames(selectedGames.filter(g => g.id !== game.id));
      // Remove spread setting when game is deselected
      const newSettings = { ...gameSpreadSettings };
      delete newSettings[game.id];
      setGameSpreadSettings(newSettings);
    } else if (selectedGames.length < 12) {
      setSelectedGames([...selectedGames, game]);
      // Default to using spread (true)
      setGameSpreadSettings({
        ...gameSpreadSettings,
        [game.id]: true
      });
    }
  };

  const handleSpreadToggle = (gameId: number, useSpread: boolean) => {
    setGameSpreadSettings({
      ...gameSpreadSettings,
      [gameId]: useSpread
    });
  };

  const handleManualSpreadUpdate = async (gameId: number, dbGameId: number) => {
    const spreadValue = manualSpreads[gameId];
    if (!spreadValue || spreadValue.trim() === '') {
      setError('Please enter a spread value');
      return;
    }

    const numericSpread = parseFloat(spreadValue);
    if (isNaN(numericSpread)) {
      setError('Spread must be a valid number');
      return;
    }

    try {
      setSpreadUpdateLoading({ ...spreadUpdateLoading, [gameId]: true });
      setError('');
      
      await gamesAPI.updateSpread(dbGameId, numericSpread);
      
      // Update the selected games with the new spread
      setSelectedGames(selectedGames.map(game => 
        game.id === gameId 
          ? { ...game, spread: numericSpread }
          : game
      ));
      
      // Clear the manual spread input
      const newManualSpreads = { ...manualSpreads };
      delete newManualSpreads[gameId];
      setManualSpreads(newManualSpreads);
      
      setSuccess(`Spread updated successfully for ${selectedGames.find(g => g.id === gameId)?.away_team} @ ${selectedGames.find(g => g.id === gameId)?.home_team}`);
      
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update spread');
    } finally {
      setSpreadUpdateLoading({ ...spreadUpdateLoading, [gameId]: false });
    }
  };

  const handleCreateWeek = async () => {
    if (weekNumber === null) {
      setError('Week number is required');
      return;
    }
    
    if (selectedGames.length < 10) {
      setError('Please select at least 10 games');
      return;
    }

    if (selectedGames.length > 12) {
      setError('Please select no more than 12 games');
      return;
    }

    if (!tiebreaker1Game || !tiebreaker2Game) {
      setError('Please select both tiebreaker games');
      return;
    }

    if (tiebreaker1Game.id === tiebreaker2Game.id) {
      setError('Tiebreaker games must be different');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Calculate lock time (1 hour before earliest game)
      if (!calculatedLockTime) {
        setError('Lock time could not be calculated. Please select games first.');
        return;
      }

      // Create the week with automatic lock time
      const newWeek = await weeksAPI.create({
        week_number: weekNumber,
        year: year,
        sport: sport,
        picks_lock_time: calculatedLockTime.toISOString()
      });
      
      setCreatedWeekId(newWeek.id);

      // Add selected games to the week
      const createdGames: (ScheduleGame & { dbId: number })[] = [];
      for (const game of selectedGames) {
        const useSpread = gameSpreadSettings[game.id] ?? true; // Default to true if not set
        const createdGame = await gamesAPI.create({
          week_id: newWeek.id,
          home_team: game.home_team,
          away_team: game.away_team,
          spread: game.spread,
          game_time: game.start_date,
          is_spread_pick: useSpread
        });
        createdGames.push({ ...game, dbId: createdGame.id });
      }

      // Update selected games with database IDs for manual spread updates
      setSelectedGames(selectedGames.map(game => {
        const createdGame = createdGames.find(cg => cg.id === game.id);
        return createdGame ? { ...game, dbId: createdGame.dbId } : game;
      }));

      // Set tiebreaker games
      const tiebreaker1DbGame = createdGames.find(g => g.id === tiebreaker1Game.id);
      const tiebreaker2DbGame = createdGames.find(g => g.id === tiebreaker2Game.id);
      
      if (tiebreaker1DbGame && tiebreaker2DbGame) {
        await tiebreakersAPI.setTiebreakers(newWeek.id, tiebreaker1DbGame.dbId, tiebreaker2DbGame.dbId);
      }

      // Week created with automatic lock time
      setSuccess(`Week ${weekNumber} created successfully with ${selectedGames.length} games and 2 tiebreakers! Picks will automatically lock at ${calculatedLockTime.tz('America/New_York').format('ddd, MMM D @ h:mm A z')} (1 hour before earliest game).`);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create week');
    } finally {
      setLoading(false);
    }
  };

  const handleLockPicks = async () => {
    if (!createdWeekId) return;

    try {
      setLockLoading(true);
      setError('');
      
      await weeksAPI.lockPicks(createdWeekId);
      setIsLocked(true);
      setSuccess('Picks have been locked! Users can no longer make changes to their picks.');
      
      // Navigate to dashboard after a brief delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (err: any) {
      console.error('Error locking picks:', err);
      setError(err.response?.data?.error || 'Failed to lock picks');
    } finally {
      setLockLoading(false);
    }
  };

  const handleUpdateRankings = async () => {
    try {
      setRankingsUpdateLoading(true);
      setError('');
      
      const result = await rankingsAPI.updateRankings();
      setSuccess(`Rankings updated successfully: ${result.message}`);
      
      // Refresh available games to reflect updated rankings
      if (weekNumber && year) {
        await loadAvailableGames();
      }
      
    } catch (err: any) {
      console.error('Error updating rankings:', err);
      setError(err.response?.data?.message || 'Failed to update rankings');
    } finally {
      setRankingsUpdateLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    // Debug: Log the original date string
    console.log('Original date string:', dateString);
    
    // Try parsing as UTC first, then convert to Eastern Time
    let date = dayjs(dateString);
    
    // If it doesn't have timezone info, assume it's UTC
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-')) {
      date = dayjs.utc(dateString);
    }
    
    const formatted = date.tz('America/New_York').format('ddd, MMM D @ h:mm A z');
    console.log('Formatted date:', formatted);
    
    return formatted;
  };

  const getGamePriorityColor = (priority: number) => {
    if (priority >= 100) return 'error'; // Top matchups
    if (priority >= 50) return 'warning'; // Good games
    return 'default'; // Regular games
  };

  const filteredAvailableGames = availableGames.filter(game =>
    !selectedGames.some(selected => selected.id === game.id)
  );

  return (
    <Box sx={{ px: { xs: 1, sm: 0 }, display: 'flex', gap: 3 }}>
      {/* Top 25 Rankings Sidebar */}
      <Card sx={{
        width: 250,
        flexShrink: 0,
        display: { xs: 'none', md: 'block' },
        maxHeight: 'calc(100vh - 120px)',
        position: 'sticky',
        top: 20
      }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEvents color="primary" />
            {rankingsPoll}
          </Typography>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            {sport === 'basketball' ? 'Men\'s Basketball' : 'College Football'}
          </Typography>
          <Divider sx={{ my: 1 }} />
          {rankingsLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : liveRankings.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
              Rankings unavailable
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
              {liveRankings.map(({ rank, team, record }) => (
                <Box
                  key={rank}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.5,
                    gap: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        minWidth: 30,
                        color: rank <= 5 ? 'primary.main' : 'text.secondary'
                      }}
                    >
                      #{rank}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {team}
                    </Typography>
                  </Box>
                  {record && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
                      {record}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              Create New Week
            </Typography>
            <Chip
              label={sport === 'football' ? '🏈 Football' : '🏀 Basketball'}
              color="primary"
              size="medium"
            />
          </Box>
        </Box>

        {authLoading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}

        {!authLoading && createPermission && !createPermission.canCreate && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Not Authorized
            </Typography>
            <Typography variant="body1" paragraph>
              {createPermission.message}
            </Typography>
            {createPermission.lastWinner && (
              <Typography variant="body2">
                <strong>{createPermission.lastWinner.username}</strong> won Week {createPermission.lastWinner.week_number} 
                and should create Week {createPermission.nextWeek?.week_number}.
              </Typography>
            )}
            <Box mt={2}>
              <Button variant="contained" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </Box>
          </Alert>
        )}

        {!authLoading && createPermission?.canCreate && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Week Configuration
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2, 
              mb: 3, 
              flexWrap: { sm: 'wrap' }
            }}>
              <TextField
                label="Year"
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                sx={{ minWidth: { xs: 'auto', sm: 120 } }}
              />
              
              <TextField
                label="Week Number"
                type="number"
                value={weekNumber || ''}
                onChange={(e) => {
                  const newWeekNumber = parseInt(e.target.value);
                  setWeekNumber(newWeekNumber);
                  // Clear selected games and reload available games for new week
                  setSelectedGames([]);
                  setTiebreaker1Game(null);
                  setTiebreaker2Game(null);
                  // loadAvailableGames will be called automatically by the useEffect
                }}
                inputProps={{ min: 1, max: 15 }}
                sx={{ minWidth: { xs: 'auto', sm: 120 } }}
              />
              
              <TextField
                label="Picks Lock Time"
                value={calculatedLockTime ? calculatedLockTime.tz('America/New_York').format('ddd, MMM D @ h:mm A z') : 'Select games to calculate'}
                InputProps={{
                  readOnly: true,
                }}
                sx={{ minWidth: { xs: 'auto', sm: 300 }, flex: { sm: 1 } }}
                helperText="Automatically set to 1 hour before earliest game"
              />
              
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<EmojiEvents />}
                onClick={handleUpdateRankings}
                disabled={rankingsUpdateLoading}
                sx={{ minWidth: { xs: 'auto', sm: 180 } }}
              >
                {rankingsUpdateLoading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Updating...
                  </>
                ) : (
                  'Update Rankings'
                )}
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Selected Games: {selectedGames.length} / 12 (minimum 10 required)
              </Typography>
              
              <Tooltip title="Update AP Top 25 rankings from ESPN to ensure accurate game prioritization">
                <Info color="action" sx={{ fontSize: 16 }} />
              </Tooltip>
            </Box>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab label={`Available Games (${filteredAvailableGames.length})`} />
                <Tab label={`Selected Games (${selectedGames.length})`} />
                <Tab label={`Tiebreakers (${(tiebreaker1Game ? 1 : 0) + (tiebreaker2Game ? 1 : 0)}/2)`} />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              {/* Basketball: Day-by-Day Picker */}
              {sport === 'basketball' ? (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body1" color="textPrimary">
                        📅 Browse games by day and select up to 12 games from any day
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showTop25Only}
                            onChange={(e) => setShowTop25Only(e.target.checked)}
                          />
                        }
                        label="Top 25 only"
                      />
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      <Info sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      Select games from any of the next 7 days. Games are sorted by priority within each day.
                    </Typography>
                  </Box>

                  <BasketballDayPicker
                    startDate={new Date()}
                    selectedGames={selectedGames}
                    onGameToggle={handleGameToggle}
                    maxGames={12}
                    showTop25Only={showTop25Only}
                  />
                </Box>
              ) : (
                /* Football: Traditional Week View */
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                      <TextField
                        label="Search teams"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="e.g., Alabama, Georgia"
                        InputProps={{
                          startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
                        }}
                        sx={{ flexGrow: 1 }}
                      />

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={showTop25Only}
                            onChange={(e) => setShowTop25Only(e.target.checked)}
                          />
                        }
                        label="Top 25 only"
                      />
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      <Info sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      Games are sorted by priority. Top 25 matchups and conference games are prioritized.
                    </Typography>
                  </Box>

                  {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                      {filteredAvailableGames.map((game) => (
                        <Card
                          key={game.id}
                          variant="outlined"
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'action.hover' },
                            opacity: selectedGames.length >= 12 ? 0.5 : 1
                          }}
                          onClick={() => handleGameToggle(game)}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center' }}>
                              <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <SportIcon sport={sport} sx={{ mr: 1 }} />
                                  <Typography variant="h6">
                                    {formatTeamWithRanking(game.away_team, game.away_ranking)} @ {formatTeamWithRanking(game.home_team, game.home_ranking)}
                                  </Typography>

                                  {game.is_top25 && (
                                    <Star sx={{ ml: 1, color: 'gold' }} />
                                  )}

                                  <Chip
                                    size="small"
                                    label={`Priority: ${game.priority}`}
                                    color={getGamePriorityColor(game.priority)}
                                    sx={{ ml: 1 }}
                                  />
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Schedule sx={{ mr: 0.5, fontSize: 16 }} />
                                    <Typography variant="body2" color="textSecondary">
                                      {formatDateTime(game.start_date)}
                                    </Typography>
                                  </Box>

                                  {game.venue && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <LocationOn sx={{ mr: 0.5, fontSize: 16 }} />
                                      <Typography variant="body2" color="textSecondary">
                                        {game.venue}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {game.conference_game && (
                                    <Chip size="small" label="Conference" variant="outlined" />
                                  )}
                                  {game.neutral_site && (
                                    <Chip size="small" label="Neutral Site" variant="outlined" />
                                  )}
                                  {game.spread ? (
                                    <Chip
                                      label={getSpreadDisplay(game)}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        bgcolor: 'info.light',
                                        color: 'info.contrastText'
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      size="small"
                                      label="No spread - manual entry available"
                                      color="warning"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </Box>

                              <IconButton
                                color="primary"
                                disabled={selectedGames.length >= 12}
                              >
                                <Add />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {selectedGames.length === 0 ? (
                <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 4 }}>
                  No games selected yet. Switch to the Available Games tab to select games.
                </Typography>
              ) : (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {selectedGames.map((game, index) => {
                    const useSpread = gameSpreadSettings[game.id] ?? true;
                    return (
                      <Card key={game.id} variant="outlined">
                        <CardContent>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: { sm: 'space-between' }, 
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            gap: { xs: 1, sm: 0 }
                          }}>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                  #{index + 1}
                                </Typography>
                                <SportIcon sport={sport} sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                  {formatTeamWithRanking(game.away_team, game.away_ranking)} @ {formatTeamWithRanking(game.home_team, game.home_ranking)}
                                </Typography>
                                {game.is_top25 && (
                                  <Star sx={{ ml: 1, color: 'gold' }} />
                                )}
                              </Box>
                              
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                {formatDateTime(game.start_date)}
                              </Typography>
                              
                              {game.spread && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                  <Chip 
                                    label={getSpreadDisplay(game)}
                                    size="small" 
                                    color="info"
                                    variant="outlined"
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      fontWeight: 600,
                                      bgcolor: 'info.light',
                                      color: 'info.contrastText'
                                    }} 
                                  />
                                  <Chip 
                                    size="small" 
                                    label={useSpread ? "Against Spread" : "Straight Pick"} 
                                    color={useSpread ? "primary" : "default"}
                                    icon={useSpread ? <TrendingUp /> : <TrendingFlat />}
                                  />
                                </Box>
                              )}
                              
                              {game.spread && (
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={useSpread}
                                      onChange={(e) => handleSpreadToggle(game.id, e.target.checked)}
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {useSpread ? <TrendingUp /> : <TrendingFlat />}
                                      <Typography variant="body2">
                                        {useSpread ? 'Use Spread' : 'Straight Pick'}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              )}
                              
                              {!game.spread && !createdWeekId && (
                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                  No spread available - will be straight pick
                                </Typography>
                              )}

                              {!game.spread && createdWeekId && (game as any).dbId && user?.is_admin && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                  <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    📊 Manual Spread Entry (Admin)
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <TextField
                                      size="small"
                                      label="Spread"
                                      placeholder="e.g., -7.5 or +3"
                                      value={manualSpreads[game.id] || ''}
                                      onChange={(e) => setManualSpreads({
                                        ...manualSpreads,
                                        [game.id]: e.target.value
                                      })}
                                      sx={{ width: 120 }}
                                      helperText="+ for underdog, - for favorite"
                                    />
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleManualSpreadUpdate(game.id, (game as any).dbId)}
                                      disabled={spreadUpdateLoading[game.id] || !manualSpreads[game.id]?.trim()}
                                    >
                                      {spreadUpdateLoading[game.id] ? <CircularProgress size={16} /> : 'Set'}
                                    </Button>
                                  </Box>
                                  <Typography variant="caption" color="textSecondary">
                                    Enter spread from {game.home_team}'s perspective
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            
                            <IconButton
                              color="error"
                              onClick={() => handleGameToggle(game)}
                              sx={{ mt: 1 }}
                            >
                              <Remove />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Typography variant="h6" gutterBottom>
                Select Tiebreaker Games
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Choose 2 games from your available games list for tiebreakers. Users will predict the total points scored in these games.
              </Typography>

              <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Tiebreaker 1 */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Tiebreaker 1
                  </Typography>
                  {tiebreaker1Game ? (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {tiebreaker1Game.away_team} @ {tiebreaker1Game.home_team}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {formatDateTime(tiebreaker1Game.start_date)}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setTiebreaker1Game(null)}
                            size="small"
                          >
                            Remove
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Select a game from your selected games
                      </Typography>
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {selectedGames
                          .filter(game => game.id !== tiebreaker2Game?.id)
                          .map((game) => (
                          <Card 
                            key={game.id} 
                            variant="outlined" 
                            sx={{ 
                              mb: 1, 
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setTiebreaker1Game(game)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Typography variant="body2">
                                {game.away_team} @ {game.home_team}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatDateTime(game.start_date)}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Tiebreaker 2 */}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Tiebreaker 2
                  </Typography>
                  {tiebreaker2Game ? (
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {tiebreaker2Game.away_team} @ {tiebreaker2Game.home_team}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {formatDateTime(tiebreaker2Game.start_date)}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setTiebreaker2Game(null)}
                            size="small"
                          >
                            Remove
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Select a game from your selected games
                      </Typography>
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {selectedGames
                          .filter(game => game.id !== tiebreaker1Game?.id)
                          .map((game) => (
                          <Card 
                            key={game.id} 
                            variant="outlined" 
                            sx={{ 
                              mb: 1, 
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                            onClick={() => setTiebreaker2Game(game)}
                          >
                            <CardContent sx={{ py: 1 }}>
                              <Typography variant="body2">
                                {game.away_team} @ {game.home_team}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatDateTime(game.start_date)}
                              </Typography>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </TabPanel>
          </CardContent>
        </Card>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                disabled={loading}
              >
                Cancel
              </Button>
              
              <Button
                variant="contained"
                onClick={handleCreateWeek}
                disabled={loading || selectedGames.length < 10 || selectedGames.length > 12 || !tiebreaker1Game || !tiebreaker2Game || createdWeekId !== null}
              >
                {loading ? <CircularProgress size={24} /> : `Create Week with ${selectedGames.length} Games & 2 Tiebreakers`}
              </Button>

              {createdWeekId && !isLocked && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<Lock />}
                  onClick={handleLockPicks}
                  disabled={lockLoading}
                  sx={{ ml: 2 }}
                >
                  {lockLoading ? <CircularProgress size={24} /> : 'Lock Picks Now'}
                </Button>
              )}

              {isLocked && (
                <Chip
                  icon={<Lock />}
                  label="Picks Locked"
                  color="success"
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
          </>
        )}
        {/* End Main Content */}
      </Box>
      {/* End Outer Container - Sidebar + Main Content */}
    </Box>
  );
};

export default CreateWeek;
